import React, { useCallback, useEffect } from 'react';
import {
    AudioSearchFilterContentTypes,
    ImageSearchFilterContentTypes,
    SearchFilterContentTypes,
    VideoSearchFilterContentTypes,
} from 'search-gateway-client/components';
import { useStatsigExperimentContext } from 'statsig/components';

import { useConversationalSearch } from '../context/NavSearchContext';

const DEFAULT_AI_PLACEHOLDER = "Describe what you need... and we'll find what fits...";

interface UseSearchWithAIOptions {
    showConversationalSearchCta: boolean;
    /**
     * The current content type. When supplied, the hook auto-disables conversational search
     * if the active content type doesn't support it (e.g. user toggles AI on for video, then
     * switches to audio). Omit when the caller has no notion of a current content type.
     */
    contentType?: SearchFilterContentTypes;
}

interface UseSearchWithAIResult {
    showAiCta: boolean;
    conversationalSearchEnabled: boolean;
    setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    getPlaceholder: (
        defaultPlaceholder: string | undefined,
        contentType: SearchFilterContentTypes,
    ) => string | undefined;
    isSearchWithAIEnabledForContentType: (contentType: string) => boolean;
}

export function useSearchWithAI({
    showConversationalSearchCta,
    contentType,
}: UseSearchWithAIOptions): UseSearchWithAIResult {
    const { conversationalSearchEnabled, setConversationalSearchEnabled } = useConversationalSearch();
    const { showConversationalSearchAudio, showConversationalSearchImages } = useStatsigExperimentContext();

    const isSearchWithAIEnabledForContentType = useCallback(
        (contentType?: string): boolean => {
            if (!showConversationalSearchCta) return false;
            if (contentType === undefined) return false;

            const permittedContentTypes = Object.values<string>(VideoSearchFilterContentTypes);
            if (showConversationalSearchAudio) {
                permittedContentTypes.push(...Object.values<string>(AudioSearchFilterContentTypes));
            }
            if (showConversationalSearchImages) {
                permittedContentTypes.push(...Object.values<string>(ImageSearchFilterContentTypes));
            }
            return permittedContentTypes.includes(contentType);
        },
        [showConversationalSearchCta, showConversationalSearchAudio, showConversationalSearchImages],
    );

    useEffect(() => {
        if (contentType === undefined) return;
        if (conversationalSearchEnabled && !isSearchWithAIEnabledForContentType(contentType)) {
            setConversationalSearchEnabled(false);
        }
    }, [
        conversationalSearchEnabled,
        contentType,
        showConversationalSearchCta,
        setConversationalSearchEnabled,
        isSearchWithAIEnabledForContentType,
    ]);

    const getPlaceholder = (
        defaultPlaceholder: string | undefined,
        contentType: SearchFilterContentTypes,
    ): string | undefined => {
        if (!conversationalSearchEnabled) {
            return defaultPlaceholder;
        }

        const placeholders: Partial<Record<SearchFilterContentTypes, string>> = {
            [SearchFilterContentTypes.All_videos_content_type]: "Describe the scene you're looking for",
            [SearchFilterContentTypes.Footage]: "Describe the footage you're looking for",
            [SearchFilterContentTypes.Motion_bgs]: 'Describe the graphic you want',
            [SearchFilterContentTypes.Templates]: "Describe the template you're looking for",
            ...(showConversationalSearchAudio && {
                [SearchFilterContentTypes.All_audio_content_type]: "Describe the audio you're looking for",
                [SearchFilterContentTypes.Music]: "Describe the music you're looking for",
                [SearchFilterContentTypes.Sound_effects]: "Describe the sound effect you're looking for",
            }),
            ...(showConversationalSearchImages && {
                [SearchFilterContentTypes.All_images_content_type]: "Describe the image you're looking for",
                [SearchFilterContentTypes.Photos]: "Describe the photo you're looking for",
                [SearchFilterContentTypes.Snapshots]: "Describe the photo you're looking for",
                [SearchFilterContentTypes.Vectors]: "Describe the vector you're looking for",
                [SearchFilterContentTypes.Illustrations]: "Describe the illustration you're looking for",
            }),
        };

        return placeholders[contentType] ?? DEFAULT_AI_PLACEHOLDER;
    };

    return {
        showAiCta: showConversationalSearchCta,
        conversationalSearchEnabled,
        setConversationalSearchEnabled,
        getPlaceholder,
        isSearchWithAIEnabledForContentType,
    };
}
